import { useTaxonomyContext } from "../contexts/Taxonomy/TaxonomyContext";
import { CompanyFilter } from "../legacy/types/companyFilter";
import { HgIndustry, HgSic, HgNaics } from "../types/taxonomy";
import { generateFilterLabel, transformToTree } from "../utils/filters/utils";
import { useHierarchyRelations } from "./useHierarchyRelations";

const generateHgIndustriesLabel = (
  selectedIds: number[],
  selectedExcludedIds: number[],
  industries: HgIndustry[]
) => {
  const industriesOptions = industries.map((industry) => ({
    id: industry?.id || 0,
    name: industry?.name || "",
  }));

  const label = generateFilterLabel(
    selectedIds,
    selectedExcludedIds,
    industriesOptions,
    "industry"
  );

  return label;
};

const generateSICsLabel = (
  selectedIds: string[],
  selectedExcludedIds: string[],
  sics: HgSic[],
  filterToHighestAncestors: (ids: string[]) => string[]
) => {
  const filteredSelectedIds = filterToHighestAncestors(selectedIds);
  const filteredExcludedIds = filterToHighestAncestors(selectedExcludedIds);

  const sicOptions = sics.map((sic) => ({
    id: sic.id,
    name: sic.name,
  }));

  const label = generateFilterLabel(
    filteredSelectedIds,
    filteredExcludedIds,
    sicOptions,
    "SIC"
  );

  return label;
};

const generateNAICSLabel = (
  selectedIds: string[],
  selectedExcludedIds: string[],
  naics: HgNaics[],
  filterToHighestAncestors: (ids: string[]) => string[]
) => {
  const filteredSelectedIds = filterToHighestAncestors(selectedIds);
  const filteredExcludedIds = filterToHighestAncestors(selectedExcludedIds);

  const naicsOptions = naics.map((naicsItem) => ({
    id: naicsItem.id,
    name: naicsItem.name,
  }));

  const label = generateFilterLabel(
    filteredSelectedIds,
    filteredExcludedIds,
    naicsOptions,
    "NAICS"
  );

  return label;
};

export const useIndustryLabel = (industryFilter: CompanyFilter["industry"]) => {
  const {
    hgIndustries: industries,
    sic: sics,
    naics: naics,
  } = useTaxonomyContext();

  // Transform SICs and NAICS to tree structure for hierarchy relations
  const sicTree = transformToTree(sics, (sic) => sic.name);
  const naicsTree = transformToTree(naics, (naics) => naics.name);

  // Use hierarchy relations for SICs and NAICS
  const { filterToHighestAncestors: filterSicsToHighestAncestors } =
    useHierarchyRelations(sicTree);
  const { filterToHighestAncestors: filterNaicsToHighestAncestors } =
    useHierarchyRelations(naicsTree);

  let label = "";

  if (
    industryFilter?.industryIds?.length ||
    industryFilter?.excludedIndustryIds?.length
  ) {
    label = generateHgIndustriesLabel(
      industryFilter.industryIds || [],
      industryFilter.excludedIndustryIds || [],
      industries
    );
  } else if (
    industryFilter?.sicUS1987Ids?.length ||
    industryFilter?.excludedSicUS1987Ids?.length
  ) {
    label = generateSICsLabel(
      industryFilter.sicUS1987Ids || [],
      industryFilter.excludedSicUS1987Ids || [],
      sics,
      filterSicsToHighestAncestors
    );
  } else if (
    industryFilter?.naics2012Ids?.length ||
    industryFilter?.excludedNaics2012Ids?.length
  ) {
    label = generateNAICSLabel(
      industryFilter.naics2012Ids || [],
      industryFilter.excludedNaics2012Ids || [],
      naics,
      filterNaicsToHighestAncestors
    );
  }

  return label;
};
